import React, { useState, useEffect } from 'react'
import { Button, Tabs, Select, Space, Collapse, Checkbox } from 'antd'
import { PlusOutlined, CloseOutlined } from '@ant-design/icons'
import { Formik, useFormikContext, getIn } from 'formik'
import styled from '@emotion/styled'
import { SiteMetadata, UF } from '../../models/CursoModel'
import api from '../../api'
import { toast } from 'react-toastify'
import { TabProvider, useTabContext } from './TabContext'
import { ListingMetadataSection } from './ListingMetadataSection'
import { SchemaMetadataSection } from './SchemaMetadataSection'
import { HeroSection } from './HeroSection'
import { MobileCTA } from './MobileCTA'
import { AboutAccreditedSection } from './AboutAccreditedSection'
import { AboutOverviewSection } from './AboutOverviewSection'
import { AboutContentSection } from './AboutContentSection'
import { AboutDetailsSection } from './AboutDetailsSection'
import { RequirementsSection } from './RequirementsSection'
import { AboutCertificationSection } from './AboutCertificationSection'
import { BenefitsSection } from './BenefitsSection'
import { AboutAdditionalInfoSection } from './AboutAdditionalInfoSection'
import { GovernmentCertificationSection } from './GovernmentCertificationSection'
import { ExpandableContentSection } from './ExpandableContentSection'
import { FaqSection } from './FaqSection'

const { Panel } = Collapse
const MAX_PIX_DISCOUNT_PERCENTAGE = 50

interface SiteMetadataEditorProps {
  cursoId: number
  initialData?: SiteMetadata
  onUpdate?: (data: SiteMetadata) => void
}

const UF_OPTIONS: { value: UF; label: string }[] = [
  { value: 'AC', label: 'Acre (AC)' },
  { value: 'AL', label: 'Alagoas (AL)' },
  { value: 'AP', label: 'Amapá (AP)' },
  { value: 'AM', label: 'Amazonas (AM)' },
  { value: 'BA', label: 'Bahia (BA)' },
  { value: 'CE', label: 'Ceará (CE)' },
  { value: 'DF', label: 'Distrito Federal (DF)' },
  { value: 'ES', label: 'Espírito Santo (ES)' },
  { value: 'GO', label: 'Goiás (GO)' },
  { value: 'MA', label: 'Maranhão (MA)' },
  { value: 'MT', label: 'Mato Grosso (MT)' },
  { value: 'MS', label: 'Mato Grosso do Sul (MS)' },
  { value: 'MG', label: 'Minas Gerais (MG)' },
  { value: 'PA', label: 'Pará (PA)' },
  { value: 'PB', label: 'Paraíba (PB)' },
  { value: 'PR', label: 'Paraná (PR)' },
  { value: 'PE', label: 'Pernambuco (PE)' },
  { value: 'PI', label: 'Piauí (PI)' },
  { value: 'RJ', label: 'Rio de Janeiro (RJ)' },
  { value: 'RN', label: 'Rio Grande do Norte (RN)' },
  { value: 'RS', label: 'Rio Grande do Sul (RS)' },
  { value: 'RO', label: 'Rondônia (RO)' },
  { value: 'RR', label: 'Roraima (RR)' },
  { value: 'SC', label: 'Santa Catarina (SC)' },
  { value: 'SP', label: 'São Paulo (SP)' },
  { value: 'SE', label: 'Sergipe (SE)' },
  { value: 'TO', label: 'Tocantins (TO)' },
]

const getInitialSections = () => ({
  hero: { heroImage: '', title: '', subtitle: '' },
  aboutAccredited: { title: '', html: '', badgeSrc: '', badgeAlt: '', showBadge: false },
  aboutOverview: { title: '', text: '', html: '' },
  aboutContent: {
    metrics: { modules: 0, lessons: 0, minDays: 0, maxDays: 0 },
    certificationText: '',
    validityText: '',
  },
  aboutDetails: { subtitle: '', learningTopics: [] },
  requirements: { subtitle: '', note: '', modules: [] },
  aboutCertification: { subtitle: '', targetAudience: [] },
  benefits: {
    opportunitiesText: '',
    opportunitiesList: [],
    prerequisitesText: '',
    prerequisites: [],
    instructionsText: '',
    instructions: [],
  },
  aboutAdditionalInfo: { additionalInfoCards: [] },
  governmentCertification: { title: '', text: '', bullets: [], note: '' },
  expandableContent: { title: '', html: '' },
  faq: { title: '', faqTabs: {} },
})

/**
 * Recursively removes empty values from an object
 * Removes: empty strings, undefined, null, empty arrays, and empty objects
 * Preserves: numbers (including 0), booleans (including false)
 */
const removeEmptyFields = (obj: any): any => {
  if (obj === null || obj === undefined || obj === '') {
    return undefined
  }

  if (Array.isArray(obj)) {
    const cleaned = obj.map((item) => removeEmptyFields(item)).filter((item) => item !== undefined)
    return cleaned.length > 0 ? cleaned : undefined
  }

  if (typeof obj === 'object') {
    const cleaned: any = {}
    Object.entries(obj).forEach(([key, value]) => {
      const cleanedValue = removeEmptyFields(value)
      if (cleanedValue !== undefined) {
        cleaned[key] = cleanedValue
      }
    })
    return Object.keys(cleaned).length > 0 ? cleaned : undefined
  }

  return obj
}

const sanitizePixDiscountPercentage = (value: any): number | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined
  }

  const numericValue = Number(value)
  if (!Number.isFinite(numericValue)) {
    return undefined
  }

  return Math.min(Math.max(Math.round(numericValue), 0), MAX_PIX_DISCOUNT_PERCENTAGE)
}

const getInitialPixDiscountPercentage = (
  data: SiteMetadata | undefined,
  fallback?: number
): number | undefined => {
  const percentage = sanitizePixDiscountPercentage(data?.pixDiscountPercentage)
  if (percentage !== undefined) return percentage

  return fallback
}

const getInitialDefaultData = (initialData?: SiteMetadata) => ({
  originalPrice: initialData?.originalPrice || '',
  finalPrice: initialData?.finalPrice || '',
  featuredOrder: initialData?.featuredOrder || '',
  customDescription: initialData?.customDescription || '',
  thumbnailImage: initialData?.thumbnailImage || '',
  courseName: initialData?.courseName || '',
  urlSlug: initialData?.urlSlug || '',
  videoId: initialData?.videoId || '',
  promoText: initialData?.promoText || '',
  productTitle: initialData?.productTitle || '',
  sku: initialData?.sku || '',
  about: initialData?.about || [],
  totalHistoricalEnrollment: initialData?.totalHistoricalEnrollment || '',
  datePublished: initialData?.datePublished || '',
  badges: initialData?.badges || [],
  isAllowed: initialData?.isAllowed ?? true,
  pixDiscountPercentage: getInitialPixDiscountPercentage(initialData, 0),
  sections: initialData?.sections || getInitialSections(),
})

const getInitialUfData = (initialData?: SiteMetadata) => {
  if (!initialData?.uf) return {}

  const ufData: Record<string, any> = {}
  Object.entries(initialData.uf).forEach(([ufKey, data]) => {
    // Only include fields that actually exist in the data, don't add defaults
    const ufEntry: any = {}

    if (data.originalPrice !== undefined) ufEntry.originalPrice = data.originalPrice
    if (data.finalPrice !== undefined) ufEntry.finalPrice = data.finalPrice
    if (data.featuredOrder !== undefined) ufEntry.featuredOrder = data.featuredOrder
    if (data.customDescription !== undefined) ufEntry.customDescription = data.customDescription
    if (data.thumbnailImage !== undefined) ufEntry.thumbnailImage = data.thumbnailImage
    if (data.courseName !== undefined) ufEntry.courseName = data.courseName
    if (data.urlSlug !== undefined) ufEntry.urlSlug = data.urlSlug
    if (data.videoId !== undefined) ufEntry.videoId = data.videoId
    if (data.promoText !== undefined) ufEntry.promoText = data.promoText
    if (data.productTitle !== undefined) ufEntry.productTitle = data.productTitle
    if (data.sku !== undefined) ufEntry.sku = data.sku
    if (data.about !== undefined) ufEntry.about = data.about
    if (data.totalHistoricalEnrollment !== undefined)
      ufEntry.totalHistoricalEnrollment = data.totalHistoricalEnrollment
    if (data.datePublished !== undefined) ufEntry.datePublished = data.datePublished
    if (data.badges !== undefined) ufEntry.badges = data.badges
    if (data.isAllowed !== undefined) ufEntry.isAllowed = data.isAllowed
    const pixDiscountPercentage = getInitialPixDiscountPercentage(data)
    if (pixDiscountPercentage !== undefined) ufEntry.pixDiscountPercentage = pixDiscountPercentage
    if (data.sections !== undefined) ufEntry.sections = data.sections

    ufData[ufKey] = ufEntry
  })
  return ufData
}

const IsAllowedCheckbox: React.FC = () => {
  const { currentTab } = useTabContext()
  const { values, setFieldValue } = useFormikContext<any>()

  const fieldPath = currentTab === 'DEFAULT' ? 'default.isAllowed' : `uf.${currentTab}.isAllowed`

  const isAllowed = getIn(values, fieldPath) ?? true

  return (
    <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid #e1e5e9' }}>
      <Checkbox checked={isAllowed} onChange={(e) => setFieldValue(fieldPath, e.target.checked)}>
        Curso disponível neste estado
      </Checkbox>
    </div>
  )
}

const AllSectionsContent: React.FC = () => (
  <>
    <IsAllowedCheckbox />
    <Collapse defaultActiveKey={['listing']} accordion>
      <Panel header="📋 Metadados de Listagem" key="listing">
        <ListingMetadataSection />
      </Panel>
      <Panel header="📌 Schemas de Marcação" key="schemas">
        <SchemaMetadataSection />
      </Panel>
      <Panel header="🎯 Hero Section" key="hero">
        <HeroSection />
      </Panel>
      <Panel header="🛒 Mobile CTA" key="cta">
        <MobileCTA />
      </Panel>
      <Panel header="📖 Sobre o Curso" key="aboutOverview">
        <AboutOverviewSection />
      </Panel>
      <Panel header="📊 Conteúdo do Curso" key="aboutContent">
        <AboutContentSection />
      </Panel>
      <Panel header="✅ Curso Credenciado" key="aboutAccredited">
        <AboutAccreditedSection />
      </Panel>
      <Panel header="🎓 Detalhes do Curso" key="aboutDetails">
        <AboutDetailsSection />
      </Panel>
      <Panel header="📚 Requisitos / Ementa" key="requirements">
        <RequirementsSection />
      </Panel>
      <Panel header="👥 Certificação" key="aboutCertification">
        <AboutCertificationSection />
      </Panel>
      <Panel header="ℹ️ Informações Adicionais" key="aboutAdditionalInfo">
        <AboutAdditionalInfoSection />
      </Panel>
      <Panel header="✨ Benefícios" key="benefits">
        <BenefitsSection />
      </Panel>
      <Panel header="🏛️ Certificação Governamental" key="governmentCertification">
        <GovernmentCertificationSection />
      </Panel>
      <Panel header="❓ FAQ" key="faq">
        <FaqSection />
      </Panel>
      <Panel header="📄 Conteúdo Expansível" key="expandableContent">
        <ExpandableContentSection />
      </Panel>
    </Collapse>
  </>
)

export const SiteMetadataEditor: React.FC<SiteMetadataEditorProps> = ({
  cursoId,
  initialData,
  onUpdate,
}) => {
  const [activeTab, setActiveTab] = useState('DEFAULT')
  const [ufTabs, setUfTabs] = useState<UF[]>(
    initialData?.uf ? (Object.keys(initialData.uf) as UF[]) : []
  )
  const [selectedUf, setSelectedUf] = useState<UF | undefined>()

  // Sync ufTabs when initialData loads or changes
  useEffect(() => {
    if (initialData?.uf) {
      const existingUfs = Object.keys(initialData.uf) as UF[]
      setUfTabs(existingUfs)
    }
  }, [initialData])

  const addUfTab = () => {
    if (selectedUf && !ufTabs.includes(selectedUf)) {
      setUfTabs([...ufTabs, selectedUf])
      setActiveTab(selectedUf)
      setSelectedUf(undefined)
    }
  }

  const removeUfTab = (uf: UF) => {
    setUfTabs(ufTabs.filter((u) => u !== uf))
    if (activeTab === uf) {
      setActiveTab('DEFAULT')
    }
  }

  const availableUfs = UF_OPTIONS.filter((opt) => !ufTabs.includes(opt.value))

  return (
    <Container>
      <Title>Metadados do Site</Title>
      <Formik
        enableReinitialize
        initialValues={{
          default: getInitialDefaultData(initialData),
          uf: getInitialUfData(initialData),
        }}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            // Build UF object from form values with deep cleaning
            const ufObject: { [K in UF]?: SiteMetadata } = {}
            Object.entries(values.uf).forEach(([ufKey, data]: [string, any]) => {
              // Only save UF data for tabs that still exist (ignore deleted tabs)
              if (!ufTabs.includes(ufKey as UF)) {
                return
              }

              const ufData = {
                thumbnailImage: data.thumbnailImage || undefined,
                courseName: data.courseName || undefined,
                urlSlug: data.urlSlug || undefined,
                videoId: data.videoId || undefined,
                promoText: data.promoText || undefined,
                customDescription: data.customDescription || undefined,
                originalPrice: data.originalPrice ? Number(data.originalPrice) : undefined,
                finalPrice: data.finalPrice ? Number(data.finalPrice) : undefined,
                featuredOrder: data.featuredOrder ? Number(data.featuredOrder) : undefined,
                productTitle: data.productTitle || undefined,
                sku: data.sku || undefined,
                about: data.about?.length > 0 ? data.about : undefined,
                totalHistoricalEnrollment: data.totalHistoricalEnrollment
                  ? Number(data.totalHistoricalEnrollment)
                  : undefined,
                datePublished: data.datePublished || undefined,
                badges: data.badges?.length > 0 ? data.badges : undefined,
                isAllowed: data.isAllowed,
                pixDiscountPercentage: sanitizePixDiscountPercentage(data.pixDiscountPercentage),
                sections: data.sections,
              }

              // Deep clean to remove all empty nested values
              const cleanedUfData = removeEmptyFields(ufData)
              if (cleanedUfData) {
                ufObject[ufKey as UF] = cleanedUfData
              }
            })

            const rawMetadata = {
              thumbnailImage: values.default.thumbnailImage || undefined,
              courseName: values.default.courseName || undefined,
              urlSlug: values.default.urlSlug || undefined,
              videoId: values.default.videoId || undefined,
              promoText: values.default.promoText || undefined,
              customDescription: values.default.customDescription || undefined,
              originalPrice: values.default.originalPrice
                ? Number(values.default.originalPrice)
                : undefined,
              finalPrice: values.default.finalPrice ? Number(values.default.finalPrice) : undefined,
              featuredOrder: values.default.featuredOrder
                ? Number(values.default.featuredOrder)
                : undefined,
              productTitle: values.default.productTitle || undefined,
              sku: values.default.sku || undefined,
              about: values.default.about?.length > 0 ? values.default.about : undefined,
              totalHistoricalEnrollment: values.default.totalHistoricalEnrollment
                ? Number(values.default.totalHistoricalEnrollment)
                : undefined,
              datePublished: values.default.datePublished || undefined,
              badges: values.default.badges?.length > 0 ? values.default.badges : undefined,
              isAllowed: values.default.isAllowed,
              pixDiscountPercentage:
                sanitizePixDiscountPercentage(values.default.pixDiscountPercentage) ?? 0,
              sections: values.default.sections,
              uf: Object.keys(ufObject).length > 0 ? ufObject : undefined,
            }

            // Deep clean the entire metadata object to remove all empty nested values
            const siteMetadata = removeEmptyFields(rawMetadata) || {}

            await api.Cursos.post({ id: cursoId, siteMetadata })
            toast.success('Metadados do site atualizados com sucesso!')
            onUpdate?.(siteMetadata)
          } catch (error) {
            toast.error('Erro ao atualizar metadados do site')
            console.error(error)
          } finally {
            setSubmitting(false)
          }
        }}
      >
        {({ handleSubmit, isSubmitting, values }) => (
          <form onSubmit={handleSubmit}>
            <TabsContainer>
              <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                type="editable-card"
                hideAdd
                tabBarExtraContent={
                  <Space>
                    <Select
                      style={{ width: 200 }}
                      placeholder="Selecione UF"
                      value={selectedUf}
                      onChange={setSelectedUf}
                      options={availableUfs}
                      disabled={availableUfs.length === 0}
                    />
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={addUfTab}
                      disabled={!selectedUf}
                    >
                      Adicionar UF
                    </Button>
                  </Space>
                }
              >
                <Tabs.TabPane tab="Padrão" key="DEFAULT" closable={false}>
                  <TabProvider tab="DEFAULT">
                    <AllSectionsContent />
                  </TabProvider>
                </Tabs.TabPane>

                {ufTabs.map((uf) => {
                  const isAllowed = values.uf?.[uf]?.isAllowed ?? true

                  return (
                    <Tabs.TabPane
                      tab={
                        <span
                          style={
                            isAllowed === false
                              ? { textDecoration: 'line-through', opacity: 0.6 }
                              : {}
                          }
                        >
                          {uf}
                          <CloseOutlined
                            style={{ marginLeft: 8 }}
                            onClick={(e) => {
                              e.stopPropagation()
                              removeUfTab(uf)
                            }}
                          />
                        </span>
                      }
                      key={uf}
                      closable={false}
                    >
                      <TabProvider tab={uf}>
                        <AllSectionsContent />
                      </TabProvider>
                    </Tabs.TabPane>
                  )
                })}
              </Tabs>
            </TabsContainer>

            <ButtonContainer>
              <Button type="primary" htmlType="submit" loading={isSubmitting} size="large">
                Atualizar Metadados do Site
              </Button>
            </ButtonContainer>
          </form>
        )}
      </Formik>
    </Container>
  )
}

const Container = styled.div`
  background: #ffffff;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 20px;
`

const Title = styled.h4`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #494d50;
  margin-bottom: 16px;
`

const TabsContainer = styled.div`
  .ant-tabs-tab-remove {
    display: none;
  }
`

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
`
